import React from 'react';
import TopNavigation from '../components/TopNavigation';
import { useTheme } from '@/contexts/ThemeContext';
import { Book } from '@/utils/bookRouting';
import StopIcon from "../components/icons/StopIcon";
import ConnectWaveIconFilled from "../components/icons/ConnectWaveIcon-Filled";

interface TutorialPageProps {
  books: Book[];
  onNavigateToExplore?: () => void;
  onNavigateToLibrary?: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToMyBooks?: () => void;
  onNavigateHome?: () => void;
  onNavigateToLogin?: (source?: string) => void;
  onNavigateToSignup?: (source?: string) => void;
  onToggleMenu: () => void;
  onBookSelect: (bookSlug: string) => void;
  isAuthenticated: boolean;
}

const TutorialPage: React.FC<TutorialPageProps> = ({ books, onNavigateToExplore, onNavigateToProfile, onNavigateToMyBooks, onNavigateToLibrary, onNavigateHome, onNavigateToLogin, onNavigateToSignup, onToggleMenu, isAuthenticated }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const themeClasses = isDarkMode ? 'text-theme-primary-dark' : 'bg-theme-light text-theme-primary-light';
  const exampleBook = books.find(b => b.title.includes("Alice's Adventures"));

  return (
    <div className={`w-full ${themeClasses} transition-colors duration-300`} style={{ backgroundColor: isDarkMode ? 'var(--background-colour-dark-mode)' : 'var(--background-colour-light-mode)' }}>
      <TopNavigation
        isDarkMode={isDarkMode}
        onToggleMenu={onToggleMenu}
        onNavigateHome={onNavigateHome}
        onNavigateToExplore={onNavigateToExplore}
        onNavigateToLibrary={onNavigateToLibrary}
        onNavigateToLogin={(source) => onNavigateToLogin?.(source)}
        onNavigateToSignup={(source) => onNavigateToSignup?.(source)}
        onNavigateToProfile={onNavigateToProfile}
        onNavigateToMyBooks={onNavigateToMyBooks}
        showProfileIcon={isAuthenticated}
      />
      <div className="w-full flex flex-col px-8 lg:px-16 xl:px-24 pb-[calc(10rem+var(--safe-bottom))]">
        <header className="text-left w-full my-24 max-w-5xl mx-auto">
          <h2 className={`portrait-text-app mb-4 text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-bold leading-tight line-height-.2 -mt-1 ${
            isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'
          }`}>
            How to use Aleph
          </h2>
          <p className={`text-lg sm:text-xl ${
            isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'
          }`}>A quick guide to get you started on your conversational reading journey.</p>
        </header>

        <div className={`w-full max-w-5xl mx-auto pt-12 pb-18 ${
          isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'
        }`}>
          <div className="space-y-16">

            {/* Step 1 */}
            <div className="flex items-center gap-8">
              <div className="shrink-0 w-40 flex justify-center">
                {exampleBook && (
                  <img
                    src={exampleBook.coverUrl}
                    alt={`${exampleBook.title} book cover`}
                    className="h-auto w-32 shadow-lg cursor-pointer hover:scale-105 transition-transform duration-200"
                  />
                )}
              </div>
              <div className="self-center">
                <h3 className="portrait-text-app text-xl sm:text-2xl font-bold mb-2">1. Navigate to a book</h3>
                <p className="text-sm sm:text-lg leading-relaxed">
                  Browse our library and select a book that piques your interest. Click on the book cover to go to its dedicated page.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-center gap-8">
              <div className="shrink-0 w-40 flex justify-center">
                <div
                  className={`w-auto px-6 py-3 rounded-full font-regular text-base transition-all duration-200
                   shadow-sm hover:shadow-md transform hover:scale-105 cursor-pointer border
                   ${
                    isDarkMode
                      ? 'border-blue-600 text-blue-400 hover:bg-blue-600 hover:text-white'
                      : 'border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white'
                  }`}
                >
                  Play
                </div>
              </div>
              <div className="self-center">
                <h3 className="portrait-text-app text-xl sm:text-2xl font-bold mb-2">2. Press "Play"</h3>
                <p className="text-sm sm:text-lg leading-relaxed">
                  Once on the book's page, press the "Play" button, and the audiobook will begin playing.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-center gap-8">
              <div className="shrink-0 w-40 flex justify-center">
                <div
                  className={`cursor-pointer controls tools-icon border-2 flex justify-center w-16 h-16 items-center
                   rounded-full p-6 aspect-square hover:scale-105 transition-transform duration-200 ${
                    isDarkMode ? 'bg-theme-light border-white-custom' : 'bg-white border-blue-700'
                  }`}
                >
                  <ConnectWaveIconFilled size="lg" color="#0000ff" className="w-8 h-10" />
                </div>
              </div>
              <div className="self-center">
                <h3 className="portrait-text-app text-xl sm:text-2xl font-bold mb-2">3. Initiate a conversation</h3>
                <p className="text-sm sm:text-lg leading-relaxed">
                  To talk to Aleph, tap the "Speak with Aleph" control in the player at any time. Your audiobook will pause and Aleph will be summoned. You may be asked to allow access to your microphone.
                </p>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex items-center gap-8">
              <div className="shrink-0 w-40 flex justify-center">
                <div className={`cursor-pointer controls tools-icon border-2 flex justify-center w-16 h-16 items-center
                 rounded-full p-6 aspect-square hover:scale-105 transition-transform duration-200 ${
                  isDarkMode ? 'bg-blue-700 border-blue-700' : 'bg-blue-700 border-blue-700'
                }`}
                >
                  <ConnectWaveIconFilled size="lg" color="#ffffff" className="w-8 h-10" />
                </div>
              </div>
              <div className="self-center">
                <h3 className="portrait-text-app text-xl sm:text-2xl font-bold mb-2">4. Wait for the beep</h3>
                <p className="text-sm sm:text-lg leading-relaxed">
                  When the control changes to "End conversation" and you hear a beep, that's your cue to start speaking. Aleph is now listening.
                </p>
              </div>
            </div>

            {/* Step 5 */}
            <div className="flex items-center gap-8">
              <div className="shrink-0 w-40 flex justify-center">
                <div className="cursor-pointer flex justify-center items-center rounded-full bg-blue-600
                hover:bg-blue-700 text-white aspect-square w-16 h-16
                 hover:scale-105 transition-transform duration-200">
                  <StopIcon className="w-11 h-11" color='white' />
                </div>
              </div>
              <div className="self-center">
                <h3 className="portrait-text-app text-xl sm:text-2xl font-bold mb-2">5. Continue reading</h3>
                <p className="text-sm sm:text-lg leading-relaxed">
                  To end your conversation you can press "End conversation", press the stop button, or just ask Aleph to continue reading.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorialPage;
